/**
 * BrandSelect — the Brand dropdown over the phone catalog's brands, so brand names stay
 * consistent. `allowAdd` (admins) adds a "+ Add new brand…" entry that reveals an inline input;
 * a name that already exists (case-insensitive) reuses that option. The current value is always
 * listed, so a brand added here survives until a model under it is saved to the catalog.
 */
import { useMemo, useState } from "react";
import { AddOptionRow } from "../ui/AddOptionRow.js";

const ADD_SENTINEL = "__add__";

export function BrandSelect({
  value,
  onChange,
  options,
  allowAdd = true,
  required = false, invalid = false, describedBy,
}: {
  value: string;
  onChange: (brand: string) => void;
  options: string[];
  allowAdd?: boolean;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
}) {
  const [adding, setAdding] = useState(false);

  const allOptions = useMemo(() => {
    const seen = new Set<string>();
    return [...options, value].filter((b) => {
      const key = b.trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [options, value]);

  const select = (next: string) => {
    setAdding(next === ADD_SENTINEL);
    if (next !== ADD_SENTINEL) onChange(next);
  };

  const add = (name: string) => {
    onChange(allOptions.find((b) => b.toLowerCase() === name.toLowerCase()) ?? name);
    setAdding(false);
  };

  return (
    <>
      <select
        className="input"
        value={adding ? ADD_SENTINEL : value}
        onChange={(e) => select(e.target.value)}
        aria-label="Brand"
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        data-testid="generate-brand"
      >
        <option value="">— Select —</option>
        {allOptions.map((b) => (
          <option key={b} value={b}>{b}</option>
        ))}
        {allowAdd && <option value={ADD_SENTINEL}>+ Add new brand…</option>}
      </select>
      {adding && <AddOptionRow placeholder="New brand name" testId="generate-brand" onAdd={add} />}
    </>
  );
}
